import { Component } from '@theme/component';
import { pbIntroBounds } from '@theme/echt-pb-box-framing';

const STAGES = ['intro', 'persona', 'sizes', 'email', 'success'];
const PANEL_STEPS = ['persona', 'sizes', 'email'];
const PROGRESS = { persona: 'Step 1 of 3: Your PB', sizes: 'Step 2 of 3: Your fit', email: 'Step 3 of 3: Early access' };
// Matches the viewer's zoom (smoothstep over 1150ms) so the fallback image moves with the same feel.
const ZOOM = { duration: 1150, easing: 'cubic-bezier(0.45, 0, 0.55, 1)' };
/**
 * Klaviyo Client API "Create Client Subscription" (public site ID only, never a private key):
 * https://developers.klaviyo.com/en/reference/create_client_subscription. Revision 2026-07-15 is the
 * current GA revision; it accepts profile `properties` and defaults consent to email marketing.
 */
const KLAVIYO = { endpoint: 'https://a.klaviyo.com/client/subscriptions', revision: '2026-07-15', source: 'PB Edit prelaunch', timeout: 10000 };

/** Aborts a request after `ms`; AbortSignal.timeout where supported. */
function deadline(ms) {
  if (AbortSignal.timeout) return AbortSignal.timeout(ms);
  const controller = new AbortController();
  setTimeout(() => controller.abort(), ms);
  return controller.signal;
}

/** Progressively enhanced native signup; email is never persisted in the browser. */
class EchtPBEdit extends Component {
  #interval;
  #target = NaN;
  #events;
  #viewer;
  #fallbackResize;
  #successLayout;
  #generation = 0;
  #stage = 'intro';
  #saved = {};
  #choices = { persona: ['runner', 'lifter', 'off-duty', 'pilates', 'mens'], top: ['xxs', 'xs', 's', 'm', 'l', 'xl', 'xxl', '3xl', '4xl', '5xl'], bottom: ['xxs', 'xs', 's', 'm', 'l', 'xl', 'xxl', '3xl', '4xl', '5xl'] };

  #selection() {
    return Object.fromEntries(Object.keys(this.#choices).map(key => [key, this.querySelector(`[name="pb-${key}"]:checked`)?.value]));
  }

  /**
   * The answers as display values (`Off-Duty`, `XL`): from the radios, or after Shopify's success reload
   * (no radios) from the restored preferences. Also the exact formats promised for the Klaviyo profile.
   */
  #answers() {
    const selection = this.#selection();
    const pick = key => selection[key] ?? this.#saved[key];
    const persona = pick('persona');
    return {
      persona: this.querySelector(`[name="pb-persona"][value="${persona}"] + span`)?.textContent.trim()
        ?? persona?.replace(/(^|-)([a-z])/g, (_, dash, letter) => dash + letter.toUpperCase()),
      top: pick('top')?.toUpperCase(),
      bottom: pick('bottom')?.toUpperCase(),
    };
  }

  #save() {
    try { sessionStorage.setItem(`pb-edit-${this.dataset.sectionId}`, JSON.stringify(this.#selection())); } catch { /* Storage is optional. */ }
  }

  #restore() {
    try {
      const saved = JSON.parse(sessionStorage.getItem(`pb-edit-${this.dataset.sectionId}`) || '{}');
      this.#saved = {};
      for (const [key, values] of Object.entries(this.#choices)) {
        if (values.includes(saved[key])) {
          this.#saved[key] = saved[key];
          const radio = this.querySelector(`[name="pb-${key}"][value="${saved[key]}"]`);
          if (radio) radio.checked = true;
        }
      }
    } catch { /* Ignore unavailable or malformed storage. */ }
  }

  /** Writes a stage into the DOM. Pure state: no measuring, no motion. */
  #render(stage, previous) {
    this.#stage = stage;
    this.dataset.stage = stage;
    this.dataset.direction = STAGES.indexOf(stage) < STAGES.indexOf(previous) ? 'back' : 'forward';
    this.toggleAttribute('data-active', stage !== 'intro');
    for (const step of this.querySelectorAll('[data-step]')) {
      step.hidden = step.dataset.step !== stage;
      step.inert = false;
      step.removeAttribute('data-leaving');
      step.removeAttribute('aria-hidden');
      step.style.removeProperty('top');
      for (const fieldset of step.querySelectorAll('fieldset')) fieldset.disabled = step.hidden;
      for (const email of step.querySelectorAll('[type="email"]')) email.disabled = step.hidden;
    }
    const panel = this.querySelector('[data-wizard-panel]');
    if (panel) panel.hidden = stage === 'intro' || stage === 'success';
    for (const confirmation of this.querySelectorAll('[data-preview-success], [data-sent-success]')) confirmation.hidden = stage !== 'success';
    const progress = this.querySelector('[data-progress]');
    if (progress) progress.hidden = !PROGRESS[stage];
    const label = this.querySelector('[data-progress-label]');
    if (label) label.textContent = PROGRESS[stage] ?? '';
    const summary = this.querySelector('[data-summary]');
    if (summary) {
      const { persona, top, bottom } = this.#answers();
      const lines = [];
      const bold = value => Object.assign(document.createElement('strong'), { textContent: value });
      if (persona) {
        const line = document.createElement('span');
        line.append('Your ', bold(persona), ' box.');
        lines.push(line);
      }
      if (top || bottom) {
        const line = document.createElement('span');
        if (top) line.append('Top ', bold(top));
        if (top && bottom) line.append(' / ');
        if (bottom) line.append('Bottom ', bold(bottom));
        lines.push(line);
      }
      summary.replaceChildren(...lines.flatMap((line, index) => index ? ['\n', line] : [line]));
      summary.hidden = lines.length === 0;
    }

  }

  /**
   * Moves to a stage. With `animate`, the box zooms from exactly where it was drawn,
   * the outgoing step fades while the next rises in, and the stage scrolls into view.
   * Reduced motion gets the same end state instantly.
   */
  #show(stage, { focus = true, animate = focus } = {}) {
    const previous = this.#stage;
    const motion = animate && stage !== previous && !matchMedia('(prefers-reduced-motion: reduce)').matches;
    const image = motion && !this.hasAttribute('data-model-ready') ? this.querySelector('.echt-pb-edit__image') : null;
    const before = image?.getBoundingClientRect();
    const leaving = motion && PANEL_STEPS.includes(previous) && PANEL_STEPS.includes(stage) ? this.querySelector(`[data-step="${previous}"]`) : null;
    const leavingTop = leaving?.offsetTop;
    const update = () => this.#render(stage, previous);

    if (this.#viewer) this.#viewer.setStage(stage, { update, instant: !motion });
    else update();

    if (leaving) this.#fadeOut(leaving, leavingTop);
    if (image && before?.width) this.#follow(image, before);
    if (animate && previous === 'intro' && stage !== 'intro') this.#reveal(motion);
    if (focus) (stage === 'intro' ? this.querySelector('[data-next="persona"]') : this.querySelector(`[data-step="${stage}"] h2, [data-success]`))?.focus({ preventScroll: true });
  }

  /** Keeps the outgoing step on top of the incoming one while it fades; it is inert and hidden from assistive tech. */
  #fadeOut(step, top) {
    step.hidden = false;
    step.inert = true;
    step.setAttribute('aria-hidden', 'true');
    step.dataset.leaving = '';
    step.style.top = `${top}px`;
    const done = () => {
      if (!step.hasAttribute('data-leaving') || step.dataset.step === this.#stage) return;
      step.hidden = true;
      step.inert = false;
      step.removeAttribute('data-leaving');
      step.removeAttribute('aria-hidden');
      step.style.removeProperty('top');
    };
    step.addEventListener('animationend', done, { once: true });
    setTimeout(done, 400);
  }

  /** Static image fallback: animate from its old place to its new one (FLIP), matching the 3D zoom. */
  #follow(image, before) {
    const after = image.getBoundingClientRect();
    if (!after.width) return;
    const dx = before.left + before.width / 2 - (after.left + after.width / 2);
    const dy = before.top + before.height / 2 - (after.top + after.height / 2);
    const scale = before.width / after.width;
    if (Math.abs(dx) < 1 && Math.abs(dy) < 1 && Math.abs(scale - 1) < .01) return;
    image.animate([{ translate: `${dx}px ${dy}px`, scale: `${scale}` }, { translate: '0 0', scale: '1' }], ZOOM);
  }

  /** Brings the stage's top edge under the sticky header when it is hidden or the stage does not fit. */
  #reveal(smooth) {
    const offset = parseFloat(getComputedStyle(this).scrollMarginTop) || 0;
    const { top, bottom } = this.getBoundingClientRect();
    if (Math.abs(top - offset) < 2 || (top >= offset && bottom <= innerHeight)) return;
    this.scrollIntoView({ block: 'start', behavior: smooth ? 'smooth' : 'auto' });
  }

  /** Sends the visitor back to the first unanswered step; the email step needs all three answers. */
  #complete() {
    const selection = this.#selection();
    for (const [key, values] of Object.entries(this.#choices)) {
      if (!values.includes(selection[key])) {
        this.#show(key === 'persona' ? 'persona' : 'sizes'); this.#valid(); return false;
      }
    }
    return true;
  }

  /**
   * Local success (static preview or the simulate_signup setting): the same stage, reveal and focus as
   * Shopify's success response, without anything being sent.
   */
  #previewSuccess() {
    if (this.dataset.preview !== 'true') return;
    this.#succeed(this.querySelector('[data-preview-success]'));
  }

  /** The success flow shared by the preview, the simulated signup and the Klaviyo signup. */
  #succeed(confirmation) {
    try { sessionStorage.removeItem(`pb-edit-${this.dataset.sectionId}`); } catch { /* Storage is optional. */ }
    this.#show('success', { focus: false, animate: true });
    confirmation?.focus({ preventScroll: true });
  }

  /** Klaviyo IDs, rendered by the section only with simulation off and both IDs set. */
  #klaviyo() {
    const company = this.dataset.klaviyoCompany?.trim();
    const list = this.dataset.klaviyoList?.trim();
    return company && list && !this.querySelector('form[data-simulated-signup]') ? { company, list } : null;
  }

  /** Busy while sending: button and Back disabled, email read-only, a polite status for screen readers. */
  #busy(on) {
    const form = this.querySelector('form');
    this.toggleAttribute('data-busy', on);
    form?.setAttribute('aria-busy', String(on));
    for (const control of this.querySelectorAll('[data-step="email"] [type="submit"], [data-email-back]')) control.disabled = on;
    const input = this.querySelector('[data-step="email"] input[type="email"]');
    if (input) input.readOnly = on;
    const status = this.querySelector('[data-send-status]');
    if (status) status.textContent = on ? 'Signing you up…' : '';
  }

  /** Shows or clears the inline send error, tying it to the email field while shown. */
  #sendError(kind) {
    const error = this.querySelector('[data-send-error]');
    const input = this.querySelector('[data-step="email"] input[type="email"]');
    if (!error || !input) return;
    const described = (input.getAttribute('aria-describedby') ?? '').split(' ').filter(id => id && id !== error.id);
    if (kind) {
      error.hidden = false;
      error.textContent = kind === 'invalid' ? error.dataset.invalid : error.dataset.failed;
      described.push(error.id);
    } else {
      error.hidden = true;
      error.textContent = '';
    }
    input.setAttribute('aria-describedby', described.join(' '));
    input.toggleAttribute('aria-invalid', kind === 'invalid');
  }

  /**
   * Subscribes the address to the Klaviyo list, with the answers as profile properties. The submit click
   * is cancelled first, so no submit event exposes the field values. The email is only ever in the request
   * body: never in storage, the URL or a log. Success only on a 2xx; anything else keeps the email step.
   */
  async #subscribe() {
    const klaviyo = this.#klaviyo();
    if (!klaviyo || this.hasAttribute('data-busy') || this.#stage !== 'email' || !this.#complete() || !this.#valid()) return;
    const input = this.querySelector('[data-step="email"] input[type="email"]');
    const { persona, top, bottom } = this.#answers();
    const body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: KLAVIYO.source,
          profile: { data: { type: 'profile', attributes: { email: input.value.trim(), properties: { pb_persona: persona, pb_top_size: top, pb_bottom_size: bottom } } } },
        },
        relationships: { list: { data: { type: 'list', id: klaviyo.list } } },
      },
    };
    this.#save();
    this.#sendError(null);
    this.#busy(true);
    let outcome = 'failed';
    try {
      const response = await fetch(`${KLAVIYO.endpoint}?${new URLSearchParams({ company_id: klaviyo.company })}`, {
        method: 'POST',
        headers: { accept: 'application/vnd.api+json', 'content-type': 'application/vnd.api+json', revision: KLAVIYO.revision },
        body: JSON.stringify(body),
        signal: deadline(KLAVIYO.timeout),
      });
      if (response.ok) outcome = 'ok';
      else if (response.status === 400 || response.status === 422) outcome = 'invalid';
    } catch { /* Network error or timeout: reported as a failure below. */ }
    this.#busy(false);
    if (!this.isConnected) return;
    if (outcome === 'ok') { this.#succeed(this.querySelector('[data-sent-success]')); return; }
    this.#sendError(outcome);
    input.focus();
  }

  /**
   * Simulated signup. The submit click is cancelled, so no submit event fires and no form data is
   * serialised or observed; the email is validated natively and never stored or sent.
   */
  #simulateSignup() {
    if (this.#stage !== 'email' || !this.#complete() || !this.#valid()) return;
    this.#previewSuccess();
  }

  #valid() {
    const inputs = this.querySelectorAll(`[data-step="${this.#stage}"] input`);
    for (const input of inputs) if (!input.reportValidity()) return false;
    return true;
  }

  async #mountViewer() {
    if (this.getAttribute('data-enable-3d') !== 'true') return;
    const generation = ++this.#generation;
    try {
      const { mountPBBox } = await import(this.dataset.viewerUrl);
      if (!this.isConnected || generation !== this.#generation) return;
      let positionConfirmation = () => {};
      const viewer = await mountPBBox(this.querySelector('[data-box-canvas]'), {
        modelUrl: this.dataset.modelUrl,
        onBounds: (stage, bounds) => positionConfirmation(stage, bounds),
        reducedMotion: matchMedia('(prefers-reduced-motion: reduce)').matches,
        stage: this.#stage,
        reference: this,
        slot: this.querySelector('[data-box-slot]'),
        onReady: () => { if (this.isConnected && generation === this.#generation) this.setAttribute('data-model-ready', ''); },
      });
      if (!this.isConnected || generation !== this.#generation) { viewer.dispose(); return; }
      this.#viewer = viewer;
      const canvas = this.querySelector('[data-box-canvas] canvas');
      positionConfirmation = (renderedStage, bounds) => {
        const content = this.querySelector('.echt-pb-edit__content');
        if (!content || (renderedStage ?? canvas?.dataset.boxStage) !== 'success') {
          this.style.removeProperty('--pb-success-lift');
          return;
        }
        const confirmation = this.querySelector('.echt-pb-edit__confirmation:not([hidden])');
        if (!confirmation) return;
        const [, storedY, , storedHeight] = canvas.dataset.boxBounds.split(' ').map(Number);
        const y = bounds?.y ?? storedY, height = bounds?.height ?? storedHeight;
        const transform = getComputedStyle(content).transform;
        const oldLift = transform === 'none' ? 0 : -new DOMMatrixReadOnly(transform).m42;
        const headingTop = confirmation.getBoundingClientRect().top + oldLift + parseFloat(getComputedStyle(confirmation).paddingTop);
        const gap = parseFloat(getComputedStyle(this).getPropertyValue('--space-7')) * parseFloat(getComputedStyle(document.documentElement).fontSize);
        const lift = Math.max(0, headingTop - (canvas.getBoundingClientRect().top + y + height + gap));
        this.style.setProperty('--pb-success-lift', `${lift}px`);
      };
      this.#successLayout = new ResizeObserver(() => positionConfirmation());
      this.#successLayout.observe(this.querySelector('.echt-pb-edit__content'));
      document.fonts.ready.then(() => requestAnimationFrame(() => { if (this.isConnected && generation === this.#generation) positionConfirmation(); }));
      positionConfirmation();
      viewer.setStage(this.#stage, { instant: true });
    } catch { this.removeAttribute('data-model-ready'); }
  }

  #setupWizard() {
    this.#events?.abort();
    this.#events = new AbortController();
    const { signal } = this.#events;
    this.#restore();
    if (this.querySelector('[data-success]')) {
      try { sessionStorage.removeItem(`pb-edit-${this.dataset.sectionId}`); } catch { /* Storage is optional. */ }
    }
    for (const element of this.querySelectorAll('[data-email-title], [data-email-back]')) element.hidden = false;
    this.#show(this.querySelector('[data-success]') ? 'success' : this.querySelector('[data-server-error]') ? 'email' : 'intro', { focus: false });
    this.addEventListener('click', event => {
      const submit = event.target.closest('form[data-simulated-signup] [type="submit"]');
      if (submit && this.contains(submit)) { event.preventDefault(); this.#simulateSignup(); return; }
      const send = this.#klaviyo() && event.target.closest('[data-step="email"] [type="submit"]');
      if (send && this.contains(send)) { event.preventDefault(); this.#subscribe(); return; }
      const button = event.target.closest('[data-next], [data-back]');
      if (!button || !this.contains(button)) return;
      if (button.dataset.next && !this.#valid()) return;
      this.#save();
      this.#show(button.dataset.next || button.dataset.back);
    }, { signal });
    this.addEventListener('change', () => this.#save(), { signal });
    this.addEventListener('input', event => { if (event.target.matches('[type="email"]')) this.#sendError(null); }, { signal });
    this.addEventListener('keydown', event => {
      if (event.key === 'Enter' && event.target.matches('input[type="radio"]')) {
        event.preventDefault();
        this.querySelector(`[data-step="${this.#stage}"] [data-next]`)?.click();
      }
    }, { signal });
    this.querySelector('form')?.addEventListener('submit', event => {
      if (this.#stage !== 'email' || this.querySelector('form[data-simulated-signup]')) { event.preventDefault(); return; }
      // A scripted submit (the click path never gets here) still goes to Klaviyo, never to both.
      if (this.#klaviyo()) { event.preventDefault(); this.#subscribe(); return; }
      if (!this.#complete()) { event.preventDefault(); return; }
      const selection = this.#selection();
      const tags = this.querySelector('[name="contact[tags]"]');
      if (tags) {
        tags.dataset.baseTags ??= tags.value;
        tags.value = [tags.dataset.baseTags, `pb-persona-${selection.persona}`, `pb-top-${selection.top}`, `pb-bottom-${selection.bottom}`].filter(Boolean).join(', ');
      }
      this.#save();
      // Native Shopify submission owns success; no fetch or optimistic confirmation.
    }, { signal });
    this.addEventListener('pb-preview-reveal', () => this.#previewSuccess(), { signal });
    this.#mountViewer();
  }

  connectedCallback() {
    super.connectedCallback();
    this.#stop();
    const hero = this.querySelector('.echt-pb-edit__hero');
    const image = this.querySelector('[data-box-fallback]');
    if (hero && image) {
      const reference = pbIntroBounds(1200, 900);
      const fit = () => {
        if (!hero.clientWidth || !hero.clientHeight) return;
        const target = pbIntroBounds(hero.clientWidth, hero.clientHeight);
        image.style.setProperty('--pb-fallback-width', `${1200 * target.width / reference.width}px`);
        image.style.setProperty('--pb-fallback-height', `${900 * target.height / reference.height}px`);
      };
      fit(); this.#fallbackResize = new ResizeObserver(fit); this.#fallbackResize.observe(hero);
    }
    this.#setupWizard();
    this.#target = Date.parse(this.dataset.launch ?? '');
    this.#tick();

    if (Number.isFinite(this.#target) && this.#target > Date.now()) {
      this.#interval = setInterval(() => this.#tick(), 1000);
    }

    this.querySelector('[data-feedback], [data-success]')?.focus({ preventScroll: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stop();
    this.#events?.abort();
    this.#generation++;
    this.#successLayout?.disconnect();
    this.#fallbackResize?.disconnect();
    this.#viewer?.dispose();
    this.#viewer = undefined;
    this.removeAttribute('data-model-ready');
  }

  #stop() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  #tick() {
    const timer = this.querySelector('[data-timer]');

    if (!Number.isFinite(this.#target)) {
      if (timer) timer.hidden = true;
      this.#stop();
      return;
    }

    const distance = Math.max(0, this.#target - Date.now());
    const total = Math.floor(distance / 1000);
    const units = {
      days: Math.floor(total / 86400),
      hours: Math.floor(total / 3600) % 24,
      minutes: Math.floor(total / 60) % 60,
      seconds: total % 60,
    };

    for (const [unit, value] of Object.entries(units)) {
      const element = this.querySelector(`[data-unit="${unit}"]`);
      if (element) element.textContent = String(value).padStart(2, '0');
    }

    if (timer) timer.hidden = false;
    if (distance === 0 && this.#stage !== 'success' && this.#stage !== 'intro') this.#show('intro', { focus: false });
    const prelaunch = this.querySelector('[data-prelaunch]');
    const launched = this.querySelector('[data-launched]');
    if (prelaunch) prelaunch.hidden = distance === 0 && this.#stage !== 'success';
    if (launched) launched.hidden = distance > 0 || this.#stage === 'success';
    if (distance === 0) this.#stop();
  }
}

if (!customElements.get('echt-pb-edit')) customElements.define('echt-pb-edit', EchtPBEdit);
